import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useCart, getCartItemUnitPrice } from '../context/CartContext';
import { resolveImageUrl } from '../context/SettingsContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import { Minus, Plus, Trash2, CalendarDays, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import BookingForm from './BookingForm';
import { buildInitialAddons } from '../utils/cartAddons';

const CartSheet = ({ children }) => {
  const { cart, removeFromCart, setItemQuantity, getTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);

  const bookingItems = useMemo(() => cart.map((item) => ({
    product: item.product,
    rental_duration: item.duration,
    quantity: item.quantity,
    with_supervision: item.withSupervision,
    // Phase 133.86 — Gewählte Preisvariante an Buchungsformular weitergeben
    price_option: item.priceOption || null,
  })), [cart]);

  // Pick the earliest event date and latest end date from cart items
  const initialDate = useMemo(() => {
    const dates = cart.map((i) => i.eventDate).filter(Boolean).sort();
    return dates[0] || null;
  }, [cart]);
  const initialDateTo = useMemo(() => {
    const dates = cart.map((i) => i.eventDateTo || i.eventDate).filter(Boolean).sort();
    return dates[dates.length - 1] || null;
  }, [cart]);

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Ihr Warenkorb ist leer');
      return;
    }
    setShowBookingForm(true);
  };

  const handleSuccess = () => {
    setShowBookingForm(false);
    setOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (!o) setShowBookingForm(false); }}>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent className="w-full sm:max-w-2xl flex flex-col overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{showBookingForm ? 'Mietanfrage abschließen' : 'Warenkorb'}</SheetTitle>
        </SheetHeader>

        {cart.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 space-y-4" data-testid="empty-cart">
            <p className="text-neutral-500">Ihr Warenkorb ist leer</p>
            <Button onClick={() => { setOpen(false); navigate('/produkte'); }} data-testid="browse-products-button" className="lift-on-hover">
              Produkte ansehen
            </Button>
          </div>
        ) : showBookingForm ? (
          <div className="flex flex-col flex-1 py-4">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowBookingForm(false)}
              className="mb-4 self-start"
              data-testid="back-to-cart-button"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Zurück zum Warenkorb
            </Button>
            <BookingForm initialItems={bookingItems} initialDate={initialDate} initialDateTo={initialDateTo} initialAddons={buildInitialAddons(cart)} onSuccess={handleSuccess} />
          </div>
        ) : (
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto py-6 space-y-4">
              {cart.map((item, index) => {
                const onRequest = !!item.product?.price_on_request;
                const price = getCartItemUnitPrice(item);
                const withoutSup = item.product?.requires_supervision && item.withSupervision === false;
                return (
                  <div
                    key={`${item.product.id}-${item.duration}-${item.eventDate}`}
                    className="bg-white rounded-3xl p-4 border border-neutral-200 space-y-3"
                    data-testid={`cart-item-${index}`}
                  >
                    <div className="flex items-start space-x-4">
                      <img
                        src={resolveImageUrl(item.product.image_url)}
                        alt={item.product.name}
                        className="h-20 w-20 rounded-2xl object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm truncate">{item.product.name}</h4>
                        <p className="text-xs text-neutral-500 mt-1">
                          {onRequest ? 'Anfrage' : (item.duration === 1 ? '1 Tag' : '3 Tage')}
                          {item.product?.requires_supervision && (
                            <span className={withoutSup ? 'text-amber-600' : 'text-emerald-600'}>
                              {' · '}{withoutSup ? 'ohne Betreuung' : 'mit Betreuung'}
                            </span>
                          )}
                        </p>
                        {item.eventDate && (
                          <p className="text-xs text-sky-600 mt-1 flex items-center">
                            <CalendarDays className="h-3 w-3 mr-1" />
                            {format(new Date(item.eventDate), 'd. MMM yyyy', { locale: de })}
                          </p>
                        )}
                        {onRequest ? (
                          <p className="text-amber-700 font-bold mt-2" data-testid={`cart-item-on-request-${index}`}>Preis auf Anfrage</p>
                        ) : (
                          <p className="text-sky-600 font-bold mt-2">€{(price * item.quantity).toFixed(2)}{item.quantity > 1 ? ` (${item.quantity}× €${price.toFixed(2)})` : ''}</p>
                        )}
                        {item.product.allow_quantity && (
                          <div className="flex items-center gap-2 mt-2" data-testid={`cart-qty-row-${index}`}>
                            <span className="text-xs text-neutral-600">Anzahl:</span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7 rounded-full"
                              onClick={() => setItemQuantity(item.product.id, item.quantity - 1)}
                              data-testid={`cart-qty-decrease-${index}`}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-7 text-center font-semibold text-sm tabular-nums" data-testid={`cart-qty-${index}`}>{item.quantity}</span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7 rounded-full"
                              onClick={() => setItemQuantity(item.product.id, item.quantity + 1)}
                              disabled={item.product.stock_quantity ? item.quantity >= Number(item.product.stock_quantity) : false}
                              data-testid={`cart-qty-increase-${index}`}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                            {item.product.stock_quantity && (
                              <span className="text-[11px] text-neutral-500 ml-1">/ max. {item.product.stock_quantity}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-full"
                        onClick={() => removeFromCart(item.product.id, item.duration, item.eventDate)}
                        data-testid={`remove-cart-item-${index}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                    {/* Phase 133.82 — Zusatzleistungen pro Produkt anzeigen,
                        falls auf der Produktseite ausgewählt. Kompakt gehalten. */}
                    {(item.deliveryAddon?.enabled || item.setupAddon?.enabled || item.cleaningAddon?.enabled) && (
                      <div className="rounded-xl bg-sky-50 border border-sky-100 px-3 py-2 text-xs space-y-1" data-testid={`cart-item-addons-${index}`}>
                        {item.deliveryAddon?.enabled && (
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="font-semibold text-sky-900">Lieferung</div>
                              <div className="text-neutral-600 truncate">
                                {[item.deliveryAddon.street, [item.deliveryAddon.zip, item.deliveryAddon.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                              </div>
                            </div>
                            <div className="text-sky-700 font-semibold whitespace-nowrap">€{Number(item.deliveryAddon.price || 0).toFixed(2)}</div>
                          </div>
                        )}
                        {item.setupAddon?.enabled && (
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-semibold text-sky-900">Auf-/Abbau inkl. Endreinigung</div>
                            <div className="text-sky-700 font-semibold whitespace-nowrap">€{(Number(item.setupAddon.price || 0) * Math.max(1, item.quantity || 1)).toFixed(2)}</div>
                          </div>
                        )}
                        {!item.setupAddon?.enabled && item.cleaningAddon?.enabled && (
                          <div className="flex items-center justify-between gap-2" data-testid={`cart-item-cleaning-${index}`}>
                            <div className="font-semibold text-sky-900">Endreinigung</div>
                            <div className="text-sky-700 font-semibold whitespace-nowrap">€{(Number(item.cleaningAddon.price || 0) * Math.max(1, item.quantity || 1)).toFixed(2)}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="border-t border-neutral-200 pt-4 space-y-3">
              <div className="flex items-center justify-between text-lg font-bold">
                <span>Zwischensumme:</span>
                <span className="text-sky-600" data-testid="cart-total">€{getTotal().toFixed(2)}</span>
              </div>
              <p className="text-xs text-neutral-500">Zusatzservices & Lieferung werden im nächsten Schritt berechnet</p>

              <Button
                className="w-full rounded-full bg-yellow-400 hover:bg-yellow-500 text-sky-900 font-bold py-6 lift-on-hover"
                onClick={handleCheckout}
                data-testid="checkout-button"
              >
                Mietanfrage abschließen
              </Button>

              <Button
                variant="outline"
                className="w-full rounded-full lift-on-hover"
                onClick={clearCart}
                data-testid="clear-cart-button"
              >
                Warenkorb leeren
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSheet;
